import { FormField, TextInput } from '@dev-spendesk/grapes';
import { type FormikProps } from 'formik';
import React from 'react';

import { CountryAutocomplete } from 'src/core/common/components/CountryAutocomplete/CountryAutocomplete';
import { useTranslation } from 'src/core/common/hooks/useTranslation';

import { type LegalDetailsFormValues } from '../../../types';

type LegalDetailsFormValuesWithPrimaryEmail = LegalDetailsFormValues & {
  primaryEmail: string;
};

type Props = FormikProps<LegalDetailsFormValuesWithPrimaryEmail>;

export const AccountPayableSuppliersPanelLegalDetailsForm = ({
  // Formik props
  values,
  handleChange,
  setFieldValue,
  errors,
}: Props) => {
  const { t } = useTranslation();

  return (
    <form className="AccountPayableSuppliersPanelLegalDetailsForm">
      <FormField
        className="my-16"
        label={t(
          'bookkeep.accountsPayable.panel.legalDetailsSection.legalNameLabel',
        )}
      >
        <TextInput
          name="legalName"
          value={values.legalName}
          placeholder={t(
            'bookkeep.accountsPayable.panel.legalDetailsSection.legalNamePlaceholder',
          )}
          isDisabled
          onChange={handleChange}
        />
      </FormField>
      <FormField
        className="my-16"
        label={t(
          'bookkeep.accountsPayable.panel.legalDetailsSection.registrationNumberLabel',
        )}
        hint={t('bookkeep.accountsPayable.panel.optionalField')}
      >
        <TextInput
          name="registrationNumber"
          value={values.registrationNumber}
          placeholder={t(
            'bookkeep.accountsPayable.panel.legalDetailsSection.registrationNumberPlaceholder',
          )}
          onChange={handleChange}
        />
      </FormField>
      <FormField
        className="my-16"
        label={t(
          'bookkeep.accountsPayable.panel.legalDetailsSection.vatNumberLabel',
        )}
        hint={t('bookkeep.accountsPayable.panel.optionalField')}
      >
        <TextInput
          name="vatNumber"
          value={values.vatNumber}
          placeholder={t(
            'bookkeep.accountsPayable.panel.legalDetailsSection.vatNumberPlaceholder',
          )}
          onChange={handleChange}
        />
      </FormField>
      <FormField
        className="my-16"
        label={t(
          'bookkeep.accountsPayable.panel.legalDetailsSection.streetAddressLabel',
        )}
        hint={t('bookkeep.accountsPayable.panel.optionalField')}
      >
        <TextInput
          name="streetAddress"
          value={values.streetAddress}
          placeholder={t(
            'bookkeep.accountsPayable.panel.legalDetailsSection.streetAddressPlaceholder',
          )}
          onChange={handleChange}
        />
      </FormField>
      <FormField
        className="my-16"
        label={t(
          'bookkeep.accountsPayable.panel.legalDetailsSection.addressLocalityLabel',
        )}
        hint={t('bookkeep.accountsPayable.panel.optionalField')}
      >
        <TextInput
          name="addressLocality"
          value={values.addressLocality}
          placeholder={t(
            'bookkeep.accountsPayable.panel.legalDetailsSection.addressLocalityPlaceholder',
          )}
          onChange={handleChange}
        />
      </FormField>
      <FormField
        className="my-16"
        label={t(
          'bookkeep.accountsPayable.panel.legalDetailsSection.postalCodeLabel',
        )}
        hint={t('bookkeep.accountsPayable.panel.optionalField')}
      >
        <TextInput
          name="postalCode"
          value={values.postalCode}
          placeholder={t(
            'bookkeep.accountsPayable.panel.legalDetailsSection.postalCodePlaceholder',
          )}
          onChange={handleChange}
        />
      </FormField>
      <FormField
        className="my-16"
        label={t(
          'bookkeep.accountsPayable.panel.legalDetailsSection.addressCountryLabel',
        )}
        hint={t('bookkeep.accountsPayable.panel.optionalField')}
      >
        <CountryAutocomplete
          fit="parent"
          name="addressCountry"
          countryCode={values.addressCountry}
          placeholder={t(
            'bookkeep.accountsPayable.panel.legalDetailsSection.addressCountryPlaceholder',
          )}
          onSelect={(selectedKey: string | undefined) =>
            setFieldValue('addressCountry', selectedKey || '')
          }
          onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
            if (event.target.value === '') {
              setFieldValue('addressCountry', '');
            }
          }}
        />
      </FormField>
      <FormField
        className="my-16"
        label={t(
          'bookkeep.accountsPayable.panel.legalDetailsSection.primaryEmailLabel',
        )}
        infoTipContent={t(
          'bookkeep.accountsPayable.panel.legalDetailsSection.primaryEmailInfoTip',
        )}
        description={t(
          'bookkeep.accountsPayable.panel.legalDetailsSection.primaryEmailDescription',
        )}
        hint={t('bookkeep.accountsPayable.panel.optionalField')}
        alertMessage={errors.primaryEmail ? t(errors.primaryEmail) : undefined}
      >
        <TextInput
          name="primaryEmail"
          value={values.primaryEmail}
          placeholder={t(
            'bookkeep.accountsPayable.panel.legalDetailsSection.primaryEmailPlaceholder',
          )}
          onChange={handleChange}
        />
      </FormField>
    </form>
  );
};
